import {
  type CreateParticipantFieldTemplateRequest,
  FieldTemplateType,
  type ParticipantFieldGroupTemplate,
} from '@piccolohealth/pbs-common';
import type { SelectOption } from '@piccolohealth/ui';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useCreateParticipantFieldTemplateMutation } from '../../../graphql/hooks/useCreateParticipantFieldTemplateMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantFieldTemplateForm } from './ParticipantFieldTemplateForm';

type FormValues = CreateParticipantFieldTemplateRequest;

const schema: z.ZodSchema<FormValues> = z.object({
  participantFieldGroupTemplateId: z.string(),
  name: z.string(),
  type: z.enum(FieldTemplateType),
  index: z.number(),
  attributes: z.any(),
});

interface Props {
  participantFieldGroupTemplateId: string;
  index: number;
  fieldGroupTemplates: ParticipantFieldGroupTemplate[];
}

export const ParticipantFieldTemplateCreateModal = createModal<Props>((props) => {
  const { participantFieldGroupTemplateId, index, fieldGroupTemplates, modal } = props;
  const { hide, remove, visible } = modal;
  const { errorToast, successToast, organization } = useAppContext();

  const initialValues: FormValues = {
    participantFieldGroupTemplateId,
    name: '',
    type: FieldTemplateType.Input,
    attributes: {},
    index,
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useCreateParticipantFieldTemplateMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      if (values.attributes.options) {
        values.attributes.options = values.attributes.options.map(
          (option: SelectOption<string>) => ({
            value: option.value,
            label: option.value,
            raw: option.value,
          }),
        );
      }

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Participant field template created successfully');
        })
        .catch((err) => {
          errorToast(`Error creating participant field template: ${err.message}`);
        });
    },
    [mutation, organization.id, hide, successToast, errorToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Create a field'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantFieldTemplateForm fieldGroupTemplates={fieldGroupTemplates} />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Create</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
